import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import en from "./locales/en";
import ja from "./locales/ja";
import { I18nContextValue } from "../types/i18n";

const LOCALES: Record<string, Record<string, unknown>> = { en, ja };
const STORAGE_KEY = "mew_lang";
const DEFAULT_LANG = "en";

const I18nContext = createContext<I18nContextValue | null>(null);

function getByPath(obj: Record<string, unknown>, path: string): unknown {
  return path.split(".").reduce<unknown>((acc, k) => {
    if (acc !== null && typeof acc === "object")
      return (acc as Record<string, unknown>)[k];
    return undefined;
  }, obj);
}

function interpolate(template: unknown, vars?: Record<string, string>): string {
  if (typeof template !== "string" || !vars) return typeof template === "string" ? template : "";
  return template.replace(/\{\{(\w+)\}\}/g, (_, k: string) =>
    vars[k] !== undefined ? vars[k] : `{{${k}}}`
  );
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<string>(() => {
    const stored = typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
    if (stored && LOCALES[stored]) return stored;
    return DEFAULT_LANG;
  });

  useEffect(() => {
    if (typeof document !== "undefined") document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((next: string) => {
    if (!LOCALES[next]) return;
    localStorage.setItem(STORAGE_KEY, next);
    setLangState(next);
  }, []);

  const t = useCallback(
    (key: string, vars?: Record<string, string>): string => {
      const dict = LOCALES[lang] || LOCALES[DEFAULT_LANG];
      let val = getByPath(dict, key);
      if (val === undefined) val = getByPath(LOCALES[DEFAULT_LANG], key);
      if (val === undefined) return key;
      return interpolate(val, vars);
    },
    [lang]
  );

  const value = useMemo<I18nContextValue>(
    () => ({ lang, setLang, t, available: Object.keys(LOCALES) }),
    [lang, setLang, t]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}

export function useT(): I18nContextValue["t"] {
  return useI18n().t;
}
